"use client"

import { ShareBar } from "@/components/data/share-bar"
import { formatPrice } from "@/lib/format-utils"

type PageType = 'product' | 'blog' | 'tool' | 'collection' | 'guide' | 'press' | 'comparison' | 'blog-index' | 'tools-index' | 'rebates' | 'about' | 'faq' | 'brand'

const SHARE_LABELS: Record<PageType, string> = {
  product: "Share Fridge.com Product:",
  blog: "Share Fridge.com Blog:",
  tool: "Share Fridge.com Tool:",
  collection: "Share Fridge.com Collection:",
  guide: "Share Fridge.com Guide:",
  press: "Share Fridge.com Press:",
  comparison: "Share Fridge.com Comparison:",
  "blog-index": "Share Fridge.com Blog:",
  "tools-index": "Share Fridge.com Tools:",
  rebates: "Share Fridge.com Rebates:",
  about: "Share Fridge.com:",
  faq: "Share Fridge.com FAQ:",
  brand: "Share on Fridge.com:",
}

interface ProductShareButtonsProps {
  productTitle: string
  productUrl?: string
  productImage?: string
  price?: number
  variant?: "top" | "bottom"
  pageType?: PageType
  customLabel?: string
}

/**
 * ProductShareButtons — thin wrapper over the shared `ShareBar` so every page
 * family (products, collections, hubs, guides, press, comparisons, filters,
 * rebates, brand hubs) renders the SAME full share rail in the SAME official
 * full-color icon style (owner spec 07/20 — the logo is the button, always
 * colorful, one line): X, Facebook, LinkedIn, Pinterest, Reddit, WhatsApp,
 * Telegram, email, copy, and native Web Share — all types, one component.
 *
 * The prop signature is unchanged so every existing caller compiles untouched.
 * `price` is folded into the share copy; `pageType` picks the leading label and
 * the utm_campaign so each family's share channel stays measurable.
 */
export function ProductShareButtons({
  productTitle,
  productUrl,
  productImage,
  price,
  pageType = "product",
  customLabel,
}: ProductShareButtonsProps) {
  const shareTitle = price ? `${productTitle} — ${formatPrice(price)}` : productTitle
  const label = customLabel || SHARE_LABELS[pageType]

  return (
    <ShareBar
      title={shareTitle}
      pageUrl={productUrl}
      imageUrl={productImage}
      variant="compact"
      label={label}
      // FRIDGE-352: the tooltip / accessible name names the thing, not the
      // price-suffixed share copy — "Share the LG LRFXS2503S on X — Fridge.com".
      subject={pageType === 'product' ? `the ${productTitle}` : productTitle}
      utmCampaign={pageType}
      utmContent="share-buttons"
    />
  )
}
